import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from '../product-health/services/config.service';
import { AuditDataComponent } from '../product-health/audit-data/audit-data.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SurveyPmRollupComponent } from './survey-pm-rollup/survey-pm-rollup.component';
import Keycloak from 'keycloak-js';
import { ConfigService as RoleConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';

type ViewMode = string;
type FieldState = Record<string, any>;

@Component({
  selector: 'app-survey-records-audits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    AuditDataComponent,
    SurveyPmRollupComponent,
  ],
  templateUrl: './survey-records-audits.component.html',
  styleUrl: './survey-records-audits.component.scss',
})
export class SurveyRecordsAuditsComponent implements OnInit {
  config: any;
  mode: ViewMode = '';
  fieldState: FieldState = {};
  entryInitialFilters: { market?: string; engine?: string; productId?: string; period?: string } = {};
  private configuredFilters: any[] = [];
  private filterRequestVersions: Record<string, number> = {};
  private rollupPeriodDefaultsApplied = false;
  private currentUserRole = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly roleConfigService: RoleConfigService,
    private readonly keycloak: Keycloak
  ) {}

  ngOnInit(): void {
    const configFile =
      this.route.snapshot.data['configFile'] ||
      'product-health/survey-records-audits.json';
    const email = String(this.keycloak.tokenParsed?.['email'] || '');

    const config$ = this.configService
      .getDashboardConfig(configFile)
      .pipe(
        switchMap((config: any) => {
          const rollupConfigFile = String(config?.rollupConfigFile || '').trim();

          if (!rollupConfigFile) {
            return of(config);
          }

          return this.configService.getDashboardConfig(rollupConfigFile).pipe(
            catchError(() => of(null)),
            map((rollupConfig: any) =>
              rollupConfig ? this.applyRollupConfig(config, rollupConfig) : config
            )
          );
        })
      );

    const role$ = this.roleConfigService.getRole(email).pipe(catchError(() => of('')));

    forkJoin({ config: config$, role: role$ }).subscribe(({ config, role }: any) => {
        this.config = config;
        this.currentUserRole = role || '';
        const visibleTabs = this.visibleTabs;
        const defaultMode =
          this.route.snapshot.data['defaultMode'] ||
          config.defaultMode ||
          visibleTabs[0]?.key ||
          '';
        this.mode = visibleTabs.some((tab: any) => tab?.key === defaultMode)
          ? defaultMode
          : visibleTabs[0]?.key || '';
        this.refreshConfiguredFilters();
        this.initializeFieldState(this.configuredFilters);
        this.applyEntryFiltersFromRoute();
        this.loadConfiguredDataSources();
        this.loadSharedFilterOptions();
        if (this.mode !== 'entry') {
          this.configuredFilters
            .filter((filter) => filter.optionsApi && !filter.dependsOn)
            .forEach((filter) => this.loadRollupFilterOptions(filter));
        }
      });
  }

  get rollupFilters(): any[] {
    return this.configuredFilters;
  }

  get visibleTabs(): any[] {
    const tabs = Array.isArray(this.config?.tabs) ? this.config.tabs : [];
    return tabs.filter((tab: any) => this.canShowForRole(tab));
  }

  get showRollupFilterBar(): boolean {
    return this.mode !== 'entry' && this.config?.rollup?.filterBar?.show !== false;
  }

  get showRollupFilterExport(): boolean {
    return this.config?.rollup?.filterBar?.showExportButton !== false && this.config?.exportButton?.show !== false;
  }

  setMode(mode: ViewMode, tab?: any): void {
    if (tab?.path && this.router.url.split('?')[0] !== tab.path) {
      this.router.navigateByUrl(tab.path);
      return;
    }

    this.mode = mode;
    this.loadRollupSummary();
  }

  onRollupFilterChange(filter: any): void {
    if (filter?.model === 'year' || filter?.model === 'period') {
      this.rollupPeriodDefaultsApplied = true;
    }
    this.configuredFilters
      .filter((dependent) => dependent.optionsApi && dependent.dependsOn === filter?.model)
      .forEach((dependent) => this.loadRollupFilterOptions(dependent));
    if (filter?.model === 'marketSegment') {
      this.loadSharedEngineOptions();
      return;
    }

    this.loadRollupSummary();
  }

  refreshRollupSummary(): void {
    this.loadRollupSummary();
  }

  filterRollupSelection(filter: any, searchText: string): void {
    filter.filteredOptions = this.filterOptions(filter.options, searchText);
  }

  openEntryResponses(product: any): void {
    const filters = this.resolveEntryFiltersFromProduct(product);
    const entryPath =
      this.config?.tabs?.find((tab: any) => tab?.key === 'entry')?.path ||
      '/survey-records-audits';

    this.entryInitialFilters = {
      ...filters,
      productId: String(product?.product_id || product?.product || ''),
      period: String(product?.period || ''),
    };
    this.router.navigate([entryPath], {
      queryParams: {
        marketSegment: filters.market || null,
        engineModel: filters.engine || null,
        productId: this.entryInitialFilters.productId || null,
        period: this.entryInitialFilters.period || null,
      },
    });
  }

  formatCell(value: number | string | null | undefined): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  entryCellClass(prop: string, value: number | null): Record<string, boolean> {
    const classMap: Record<string, boolean> = {};
    const rules = this.config?.scoreConfig?.entryCellClasses || [];

    rules.forEach((rule: any) => {
      const fields = Array.isArray(rule.fields) ? rule.fields : [];
      const values = Array.isArray(rule.equals) ? rule.equals : [];
      if (fields.includes(prop) && values.includes(value)) {
        classMap[rule.className] = true;
      }
    });

    return classMap;
  }

  isEntryScoreColumn(prop: string): boolean {
    return (this.config?.scoreConfig?.scoreFields || []).includes(prop);
  }

  trackByProp(_index: number, item: any): string {
    return item?.prop || item?.key || item?.label || item?.title || String(_index);
  }

  private initializeFieldState(filters: any[]): void {
    filters.forEach((filter) => {
      this.fieldState[filter.model] = filter.defaultValue ?? '';
    });
  }

  private applyEntryFiltersFromRoute(): void {
    const query = this.route.snapshot.queryParamMap;
    const market = query.get('marketSegment') || query.get('market') || '';
    const engine = query.get('engineModel') || query.get('engine') || '';
    const productId = query.get('productId') || '';
    const period = query.get('period') || '';

    if (market || engine) {
      this.entryInitialFilters = { market, engine, productId, period };
    }

    const readAction = this.config?.rollup?.viewResponsesAction;
    if (productId && period && readAction?.payload) {
      this.config.entry.auditData.api = {
        ...(this.config.entry.auditData.api || {}),
        read: readAction,
      };
    }
  }

  private canShowForRole(item: any): boolean {
    const visibleRoles = Array.isArray(item?.visibleRoles) ? item.visibleRoles : [];
    return !visibleRoles.length || visibleRoles.includes(this.currentUserRole);
  }

  private resolveEntryFiltersFromProduct(product: any): { market: string; engine: string } {
    const productName = String(product?.product || product?.product_id || '').trim();
    const markets = this.getEntryMarketOptions();
    const matchedMarket = markets
      .sort((a, b) => b.length - a.length)
      .find((market) => productName.toLowerCase().endsWith(` ${market.toLowerCase()}`));

    if (matchedMarket) {
      return {
        market: matchedMarket,
        engine: productName.slice(0, -matchedMarket.length).trim(),
      };
    }

    const [engine, ...marketParts] = productName.split(/\s+/);
    return {
      market: marketParts.join(' '),
      engine: engine || productName,
    };
  }

  private getEntryMarketOptions(): string[] {
    const options = this.config?.entry?.auditData?.options?.market;
    return Array.isArray(options)
      ? options.map((item: any) => String(item || '').trim()).filter(Boolean)
      : [];
  }

  private getConfiguredFilters(): any[] {
    const sourceConfig = this.config?.rollup?.filterSource;
    const sourceRoot = sourceConfig?.path ? this.readPath(this.config, sourceConfig.path) : null;
    const rollupFilters = Array.isArray(this.config?.rollup?.filters)
      ? this.config.rollup.filters
      : [];
    const sourceFields = rollupFilters.length
      ? rollupFilters
      : Array.isArray(sourceConfig?.fields)
        ? sourceConfig.fields
        : [];

    if (sourceRoot && sourceFields.length) {
      return sourceFields
        .filter((field: any) => {
          const showPath = String(field?.showPath || '').trim();
          return !showPath || this.readPath(sourceRoot, showPath) !== false;
        })
        .map((field: any) => {
          const sourceOptions = this.readPath(sourceRoot, field.optionsPath);
          const configuredOptions = field.options ?? [];
          const options =
            Array.isArray(sourceOptions) && sourceOptions.length === 0 && configuredOptions.length
              ? configuredOptions
              : sourceOptions ?? configuredOptions;
          const normalizedOptions = this.withAllOption(options, field.allLabel);

          return {
            ...field,
            label: this.readPath(sourceRoot, field.labelPath) || field.label || '',
            model: field.model,
            type: field.type || 'select',
            defaultValue:
              field.defaultValue ??
              this.readPath(sourceRoot, field.defaultValuePath) ??
              'All',
            options: normalizedOptions,
            filteredOptions: normalizedOptions,
            enableSearch: field.enableSearch ?? false,
            searchPlaceholder: field.searchPlaceholder ?? 'Search',
            noEntriesFoundLabel: field.noEntriesFoundLabel ?? 'No entries found',
            placeholder: field.placeholder,
          };
        });
    }

    return Array.isArray(this.config?.filters) ? this.config.filters : [];
  }

  private loadConfiguredDataSources(): void {
    const sources = (this.config?.dataSources || []).filter((source: any) => !!source.apiUrl);
    if (!sources.length) return;

    forkJoin(
      sources.map((source: any) =>
        this.configService
          .customApi(source.apiUrl, source.payload || {}, source.method || 'post')
          .pipe(catchError(() => of(null)))
      )
    ).subscribe((responses: any) => {
      responses.forEach((response: any, index: number) => {
        if (!response) return;

        const source = sources[index];
        const value = this.extractResponseValue(response, source.responseKey);
        if (value !== undefined) {
          this.writePath(this.config, source.targetPath, value);
        }
      });
      this.applyFilterDefaults();
    });
  }

  private applyFilterDefaults(): void {
    this.configuredFilters.forEach((filter: any) => {
      filter.options = this.withAllOption(filter.options, filter.allLabel);
      filter.filteredOptions = this.filterOptions(filter.options, '');
      if (this.fieldState[filter.model] !== undefined && this.fieldState[filter.model] !== null) {
        return;
      }

      this.fieldState[filter.model] = filter.defaultValue ?? 'All';
    });
  }

  private withAllOption(options: any[], allLabel = 'All'): any[] {
    const values = Array.isArray(options) ? options : [];
    const label = String(allLabel || 'All');
    const hasAll = values.some((option) => String(option).trim().toLowerCase() === label.toLowerCase());
    return hasAll ? [...values] : [label, ...values];
  }

  private filterOptions(options: any[], searchText: string): any[] {
    const values = Array.isArray(options) ? options : [];
    const query = String(searchText ?? '').trim().toLowerCase();

    if (!query) return [...values];

    return values.filter((option) => String(option).toLowerCase().includes(query));
  }

  private loadSharedFilterOptions(): void {
    const optionsConfig = this.config?.entry?.auditData?.options;
    if (!optionsConfig) {
      this.applyFilterDefaults();
      this.loadRollupSummary();
      return;
    }

    const marketApiConfig = optionsConfig.marketApi || {};
    if (marketApiConfig.enabled === false) {
      this.applyFilterDefaults();
      this.loadSharedEngineOptions();
      return;
    }

    this.configService
      .getMarketSegments()
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const responsePath =
          marketApiConfig.responsePath || 'responseList.0.responseData.0.segments';
        const markets = this.readPath(response, responsePath);

        if (Array.isArray(markets) && markets.length) {
          optionsConfig.market = [...markets];
        }

        this.refreshConfiguredFilters();
        this.applyFilterDefaults();
        this.loadSharedEngineOptions();
      });
  }

  private loadSharedEngineOptions(): void {
    const optionsConfig = this.config?.entry?.auditData?.options;
    if (!optionsConfig) return;

    const selectedMarket = String(this.fieldState['marketSegment'] || '').trim();
    const engineApiConfig = optionsConfig.engineApi || {};
    const marketFilter = selectedMarket && selectedMarket !== 'All' ? [selectedMarket] : undefined;

    if (engineApiConfig.enabled === false) {
      this.applyEngineDefault(optionsConfig.engine);
      this.loadRollupSummary();
      return;
    }

    this.configService
      .getEngineModels(marketFilter)
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const responsePath =
          engineApiConfig.responsePath || 'responseList.0.responseData.0.engineModels';
        const engines = this.readPath(response, responsePath);

        if (Array.isArray(engines) && engines.length) {
          optionsConfig.engine = [...engines];
        }

        this.refreshConfiguredFilters();
        this.applyEngineDefault(optionsConfig.engine);
        this.loadRollupSummary();
      });
  }

  private applyEngineDefault(engines: any[]): void {
    const engineFilter = this.configuredFilters.find((filter: any) => filter?.model === 'engineModel');
    const allLabel = engineFilter?.allLabel || 'All';
    const engineOptions = this.withAllOption(engines, allLabel);
    const currentEngine = this.fieldState['engineModel'];

    if (currentEngine === undefined || currentEngine === null || currentEngine === '') {
      this.fieldState['engineModel'] = engineFilter?.defaultValue ?? allLabel;
      return;
    }

    if (!engineOptions.includes(currentEngine)) {
      this.fieldState['engineModel'] = engineFilter?.defaultValue ?? allLabel;
    }
  }

  private refreshConfiguredFilters(): void {
    this.configuredFilters = this.getConfiguredFilters();
  }

  private loadRollupFilterOptions(filter: any, preferredValue?: string): void {
    const api = filter.optionsApi;
    if (!api?.payload) return;

    const model = filter.model;
    const requestVersion = (this.filterRequestVersions[model] || 0) + 1;
    this.filterRequestVersions[model] = requestVersion;
    const dependency = filter.dependsOn ? this.fieldState[filter.dependsOn] : undefined;
    const configuredFilter = this.config.rollup.filters.find((item: any) => item.model === model);
    configuredFilter.options = [];
    this.fieldState[model] = preferredValue ?? filter.defaultValue ?? 'All';
    if (preferredValue) configuredFilter.options = [preferredValue];
    this.refreshConfiguredFilters();

    if (filter.dependsOn && (!dependency || dependency === 'All')) return;

    // Dropdown requests use only their configured filter, without market/engine summary filters.
    const resolve = (value: any): any => {
      if (Array.isArray(value)) return value.map(resolve);
      if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolve(item)]));
      }
      if (typeof value !== 'string') return value;
      return value.replace(/\{\{\s*filters\.([^}]+?)\s*\}\}/g, (_match, key) =>
        String(this.fieldState[String(key).trim()] ?? '')
      );
    };

    this.configService.customApi(api.url || '/spriced/platform', resolve(api.payload), api.method || 'post')
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        if (this.filterRequestVersions[model] !== requestVersion) return;
        const paths = api.responsePaths || [api.responsePath];
        const keys = api.valueKeys || [model, 'value'];
        const extract = (source: any): string[] => {
          if (Array.isArray(source)) return source.flatMap(extract);
          if (source && typeof source === 'object') {
            const key = keys.find((key: string) => source[key] !== undefined);
            return key ? extract(source[key]) : [];
          }
          return source === undefined || source === null || source === '' ? [] : [String(source)];
        };
        const values: string[] = paths.map((path: string) => extract(this.readPath(response, path)))
          .find((options: string[]) => options.length) || [];
        configuredFilter.options = [...new Set(values)];
        const currentValue = String(this.fieldState[model] ?? '');
        if (preferredValue && model === 'period' && currentValue === preferredValue) {
          const year = String(this.fieldState['year']);
          const matchingValue = values.find((value) => {
            const normalized = value.replace(/\s+/g, '').toUpperCase();
            return normalized === preferredValue || normalized === `${preferredValue}${year}`;
          });
          this.fieldState[model] = matchingValue || preferredValue;
        }
        const selectedValue = String(this.fieldState[model] ?? currentValue);
        if (selectedValue && selectedValue !== 'All' && !configuredFilter.options.includes(selectedValue)) {
          configuredFilter.options.push(selectedValue);
        }
        this.refreshConfiguredFilters();
      });
  }

  private applyRollupConfig(config: any, rollupConfig: any): any {
    const { styleVars, rollup, ...rollupRoot } = rollupConfig || {};

    if (styleVars) {
      config.styleVars = {
        ...(config.styleVars || {}),
        ...styleVars,
      };
    }

    config.rollup = rollup || rollupRoot;
    return config;
  }

  private loadRollupSummary(): void {
    const readAction = this.config?.rollup?.readAction;
    if (!readAction?.payload || this.mode === 'entry') return;

    const payload = this.buildConfiguredPayload(readAction.payload);
    const url = readAction.url || '/spriced/platform';
    const method = readAction.method || 'post';

    this.configService
      .customApi(url, payload, method)
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const summary = this.extractRollupSummary(response, readAction);
        const rows = this.extractRollupRows(response, readAction);
        const products = rows.map((row: any, index: number) =>
          this.mapRollupProduct(row, readAction.productMapping, readAction.rowDefaults, index)
        );
        this.config.rollup.products = products;
        this.applyRollupMetrics(products, summary);
        this.applyRollupPeriod(summary);
        this.initializeRollupPeriodFilters(summary, products);
        this.config.rollup.toolbar = {
          ...(this.config.rollup.toolbar || {}),
          rangeLabel: `Showing ${rows.length ? 1 : 0}-${rows.length} of ${rows.length}`,
        };
        this.config.rollup.selection = {
          ...(this.config.rollup.selection || {}),
          baseSelectedCount: 0,
        };
      });
  }

  private applyRollupMetrics(products: any[], summarySource?: any): void {
    const metrics = Array.isArray(this.config?.rollup?.metrics) ? this.config.rollup.metrics : [];
    if (!metrics.length) return;

    const publishedStatuses = new Set(
      (this.config?.rollup?.metricRules?.publishedStatuses || [])
        .map((status: any) => String(status).trim().toLowerCase())
        .filter(Boolean)
    );
    const lowConfidenceThreshold = Number(this.config?.rollup?.metricRules?.lowConfidenceScoreBelow);

    const summary: Record<string, number> = {
      totalProductsTracked: this.toNumberOrNull(summarySource?.totalProductsTracked) ?? products.length,
      productsPublished:
        this.toNumberOrNull(summarySource?.productsPublished) ??
        (publishedStatuses.size
          ? products.filter((product) => publishedStatuses.has(String(product?.status || '').trim().toLowerCase())).length
          : 0),
      lowConfidenceProducts:
        this.toNumberOrNull(summarySource?.lowConfidenceProducts) ??
        (Number.isFinite(lowConfidenceThreshold)
          ? products.filter((product) => {
              const score = Number(product?.avgScore);
              return Number.isFinite(score) && score < lowConfidenceThreshold;
            }).length
          : 0),
    };

    this.config.rollup.metrics = metrics.map((metric: any) => {
      const valuePath = String(metric?.valuePath || '').trim();
      const summaryValue = valuePath ? this.readPath({ summary }, valuePath) : summary[metric?.key];
      return {
        ...metric,
        value: summaryValue ?? 0,
      };
    });
  }

  private applyRollupPeriod(summary: any): void {
    if (!summary || typeof summary !== 'object') return;

    this.config.rollup.periodMenu = {
      ...(this.config.rollup.periodMenu || {}),
      defaultPeriod: summary.period || this.config.rollup.periodMenu?.defaultPeriod || '',
      rangeLabel: summary.periodLabel || this.config.rollup.periodMenu?.rangeLabel || '',
      year: summary.year || this.config.rollup.periodMenu?.year,
      availableYears: Array.isArray(summary.availableYears)
        ? summary.availableYears
        : this.config.rollup.periodMenu?.availableYears,
      half: summary.half ?? this.config.rollup.periodMenu?.half,
      availablePeriods: Array.isArray(summary.availablePeriods)
        ? summary.availablePeriods
        : this.config.rollup.periodMenu?.availablePeriods,
    };
  }

  private initializeRollupPeriodFilters(summary: any, products: any[]): void {
    if (this.rollupPeriodDefaultsApplied) return;
    const source = summary?.year || summary?.period ? summary : products[0];
    const periodText = String(source?.period || summary?.periodLabel || '').replace(/\s+/g, '');
    const match = periodText.match(/^H([12])(\d{4})$/i);
    const year = String(source?.year || match?.[2] || '');
    const half = String(source?.half ?? match?.[1] ?? '').replace(/^H/i, '');
    if (!year || !['1', '2'].includes(half)) return;

    const yearFilter = this.config.rollup.filters?.find((filter: any) => filter.model === 'year');
    const periodFilter = this.config.rollup.filters?.find((filter: any) => filter.model === 'period');
    if (!yearFilter || !periodFilter) return;

    this.rollupPeriodDefaultsApplied = true;
    this.fieldState['year'] = year;
    yearFilter.options = [...new Set([...(yearFilter.options || []), year])];
    this.refreshConfiguredFilters();
    this.loadRollupFilterOptions(periodFilter, `H${half}`);
  }

  private buildConfiguredPayload(payloadTemplate: any): any {
    let payloadStr = JSON.stringify(payloadTemplate ?? {});
    const context = {
      filters: this.fieldState,
      market: this.fieldState['marketSegment'],
      engine: this.fieldState['engineModel'],
    };
    payloadStr = payloadStr.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, token) =>
      String(this.readPath(context, String(token).trim()) ?? '')
    );

    const payload = JSON.parse(payloadStr);
    const filter = payload?.requestData?.filter ?? {};
    const nextFilter: Record<string, any> = {};

    Object.entries(filter || {}).forEach(([key, value]) => {
      if (key === 'market_segment' || key === 'engine_model') return;
      if (value !== undefined && value !== null && value !== '' && value !== 'All') {
        nextFilter[key] = value;
      }
    });

    const marketValue = String(this.fieldState['marketSegment'] ?? '').trim();
    const engineValue = String(this.fieldState['engineModel'] ?? '').trim();

    if (marketValue && marketValue !== 'All') {
      nextFilter['market_segment'] = marketValue;
    }

    if (engineValue && engineValue !== 'All') {
      nextFilter['engine_model'] = engineValue;
    }

    payload.requestData = payload.requestData || {};
    payload.requestData.filter = nextFilter;

    return payload;
  }

  private extractRollupRows(response: any, readAction: any): any[] {
    const configuredRows = this.readPath(response, readAction.responsePath || '');
    const fallbackRows =
      configuredRows ??
      this.readPath(response, 'responseList.0.responseData') ??
      this.readPath(response, 'responseList.0.survey_history.0.products') ??
      this.readPath(response, 'responseList.0.survey_history.products') ??
      this.readPath(response, 'responseData') ??
      this.readPath(response, 'data.responseData') ??
      [];

    if (Array.isArray(fallbackRows)) return fallbackRows;
    if (fallbackRows && typeof fallbackRows === 'object') {
      const nestedRows = Object.values(fallbackRows).find((value) => Array.isArray(value));
      return Array.isArray(nestedRows) ? nestedRows : [fallbackRows];
    }

    return [];
  }

  private extractRollupSummary(response: any, readAction: any): any {
    const configuredSummary = this.readPath(response, readAction.summaryPath || '');
    return (
      configuredSummary ??
      this.readPath(response, 'responseList.0.survey_history.0') ??
      this.readPath(response, 'responseList.0.responseData.0') ??
      null
    );
  }

  private mapRollupProduct(row: any, mapping: any = {}, rowDefaults: any = {}, index: number): any {
    const readValue = (paths: string[], fallback: any = '') => {
      for (const path of paths) {
        const value = this.readPath(row, path);
        if (value !== undefined && value !== null && value !== '') return value;
      }
      return fallback;
    };

    const product = readValue(mapping.product || ['product', 'product_id', 'productId', 'name']);
    const avgScore = this.toNumberOrNull(readValue(mapping.avgScore || ['avgScore', 'avg_score', 'average_score'], null));
    const anomaly = readValue(mapping.anomaly || ['anomalyCount', 'anomaly_count', 'anomaly', 'anomaly_score'], null);
    const responses = readValue(mapping.responses || ['responses', 'response_count', 'responseCount'], 0);
    const responseCount = this.toNumberOrNull(responses);
    const year = this.toNumberOrNull(readValue(mapping.year || ['year', 'currentYear', 'reviewYear'], null));
    const half = this.toNumberOrNull(readValue(mapping.half || ['half', 'currentHalf', 'reviewHalf'], null));
    const avgOverriddenValue = readValue(mapping.avgOverridden || ['avgOverridden', 'avg_overridden'], false);
    const avgOverridden = avgOverriddenValue === true || String(avgOverriddenValue).toLowerCase() === 'true';
    const avgScoreChange = this.toNumberOrNull(
      readValue(mapping.avgScoreChange || mapping.scoreDelta || ['avgScoreChange', 'scoreDelta', 'score_delta', 'delta'], null)
    );
    const originalAvgScore = this.toNumberOrNull(
      readValue(mapping.originalAvgScore || ['originalAvgScore', 'original_avg_score'], null)
    );
    const insufficientData = readValue(mapping.insufficientData || ['insufficientData', 'insufficient_data'], null);

    const dimensionAverages = this.resolveDimensionAverages(
      readValue(mapping.dimensionAverages || ['dimensionAverages', 'dimension_averages', 'perDimensionAverages'], null)
    );
    const trend = this.resolveRollupTrend(
      readValue(mapping.trend || ['trend', 'trendData', 'trend_data'], null)
    );
    const metadata = readValue(mapping.metadata || ['metadata'], null);

    return {
      id: readValue(mapping.id || ['id', 'product_id', 'productId'], product || String(index)),
      product,
      product_id: product,
      period: row?.period,
      avgFrom: row?.avgFrom ?? row?.avg_from,
      competitor: readValue(mapping.competitor || ['competitor', 'keyCompetitor', 'key_competitor', 'keycompetitor']),
      responses,
      avgScore,
      scoreDelta: avgScoreChange,
      avgScoreChange,
      anomaly,
      outlier: Boolean(readValue(mapping.outlier || ['outlier', 'is_outlier', 'isAnomaly'], false)),
      alert: responseCount !== null && responseCount < 5,
      status: readValue(mapping.status || ['status', 'review_status'], rowDefaults.status || ''),
      year,
      half,
      availableYears: readValue(mapping.availableYears || ['availableYears', 'available_years'], []),
      availablePeriods: readValue(mapping.availablePeriods || ['availablePeriods', 'available_periods'], []),
      avgSource: readValue(mapping.avgSource || ['avgSource', 'avg_source'], null),
      avgOverridden,
      originalAvgScore,
      avgYear: this.toNumberOrNull(readValue(mapping.avgYear || ['avgYear', 'avg_year'], null)),
      avgPeriod: readValue(mapping.avgPeriod || ['avgPeriod', 'avg_period'], null),
      dimensionAverages,
      perDimensionAverages: dimensionAverages,
      insufficientData,
      trend,
      metadata,
    };
  }

  private resolveDimensionAverages(source: any): any {
    if (Array.isArray(source)) return source;
    if (!source || typeof source !== 'object') return null;

    return Object.entries(source).map(([key, value]) => {
      const numeric = Number(value);
      return {
        label: key,
        value,
        width: Number.isFinite(numeric) ? Math.max(0, Math.min(100, numeric * 10)) : 0,
        color: Number.isFinite(numeric) && numeric >= 6 ? 'green' : 'yellow',
      };
    });
  }

  private resolveRollupTrend(source: any): any {
    if (!Array.isArray(source) || !source.length) return source;

    const baseTrend = this.config?.rollup?.trend || {};
    return {
      ...baseTrend,
      chartData: {
        ...(baseTrend.chartData || {}),
        category: source.map((item) => item?.label || item?.period),
        legend: ['TCO', 'Avg Score'],
        series: [
          {
            ...(baseTrend.chartData?.series?.[0] || {}),
            name: 'TCO',
            type: 'bar',
            data: source.map((item) => item?.perDimensionAverages?.TCO ?? null),
          },
          {
            ...(baseTrend.chartData?.series?.[1] || {}),
            name: 'Avg Score',
            type: 'line',
            data: source.map((item) => item?.avgScore ?? null),
          },
        ],
      },
    };
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private extractResponseValue(response: any, responseKey?: string): any {
    if (!responseKey) return response;

    return responseKey.split('.').reduce((current: any, key: string) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, response);
  }

  private readPath(source: any, path: string): any {
    return String(path || '').split('.').reduce((current: any, key: string) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, source);
  }

  private writePath(target: any, path: string, value: any): void {
    const keys = String(path || '').split('.').filter(Boolean);
    if (!keys.length) return;

    const finalKey = keys.pop() as string;
    const parent = keys.reduce((current: any, key: string) => {
      current[key] = current[key] ?? {};
      return current[key];
    }, target);

    parent[finalKey] = value;
  }
}
